import { z } from "zod";
import { request } from "@/api/client";

// Mirrors `src/core/appstore/catalog.py`'s `APPSTORE_PRODUCT_IDS` exactly —
// the single frontend source of truth for the `PaymentProductCode` ->
// App Store `productId` mapping. There is no shared codegen for this table
// (see the backend module's docstring); keep the two in sync by hand.
// DUE-529: the App Store sells one subscription group with two monthly
// levels — Relation and Words. Every earlier SKU (pair1, submonth,
// transitmonth, annual, relationnamed, relationwords) is gone here, in the
// backend table and in `ios/App/Duetto.storekit`.
export const APPSTORE_PRODUCT_CODES = ["SUB_RELATION", "SUB_WORDS"] as const;
export type AppStoreProductCode = (typeof APPSTORE_PRODUCT_CODES)[number];

export const APPSTORE_PRODUCT_IDS: Record<AppStoreProductCode, string> = {
  SUB_RELATION: "app.psyna.relation.monthly",
  SUB_WORDS: "app.psyna.words.monthly",
};

export function isAppStoreProductCode(code: string): code is AppStoreProductCode {
  return Object.prototype.hasOwnProperty.call(APPSTORE_PRODUCT_IDS, code);
}

// Response of `POST /api/payments/appstore/verify` — see
// `src/api/schemas/appstore.py::AppStoreVerifyResponse`.
export const AppStoreVerifyResponseSchema = z.object({
  status: z.string(),
  product_code: z.string(),
  // True the first time this Apple transaction was fulfilled; false on a
  // replay (client retry, Transaction.updates redelivery) where the
  // payment already existed and grants were not re-applied. Replays are
  // still a "success" response (200) — this is informational, not an
  // error signal.
  granted: z.boolean(),
  // DUE-470 (restore only). The frontend keeps this local zod schema (it does
  // not import shared/api-types.ts); the server sends `transferred` and `active`
  // on every response, defaulting to false outside Restore.
  // `transferred`: the subscription was moved to this account from the guest
  // that first bought it. `active`: already on this account and still valid.
  transferred: z.boolean().optional(),
  active: z.boolean().optional(),
});
export type AppStoreVerifyResponse = z.infer<typeof AppStoreVerifyResponseSchema>;

/**
 * Hands a StoreKit 2 signed transaction (JWS) to the backend for
 * verification + fulfillment. Throws `ApiError` (via `request()`) on a
 * rejected/unknown-product transaction — callers treat that as a failed
 * purchase, not a thrown-and-uncaught bug.
 */
export async function verifyAppStorePurchase(
  jws: string,
  opts: { restore?: boolean } = {},
): Promise<AppStoreVerifyResponse> {
  return request({
    method: "POST",
    path: "/api/payments/appstore/verify",
    body: opts.restore ? { jws, restore: true } : { jws },
    schema: AppStoreVerifyResponseSchema,
  });
}
